import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { Theme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TOP_NAV_HEIGHTUpMd } from "../../_shared/Header";
import { ARTICLE_SECTION_OFFSET } from "../constants";
import {
  IContentItem,
  IConvertedArticleContent,
} from "../../learningCenter/interfaces";

export const ArticleMainContent = ({
  items,
  setActiveItemId,
}: {
  items: IConvertedArticleContent[];
  setActiveItemId: Dispatch<SetStateAction<string | null>>;
}) => {
  const { i18n, t } = useTranslation(["learningCenter"]);
  const isRTL = i18n.dir() === "rtl";

  const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const downLg = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const observerOptions = {
      rootMargin: `-${
        TOP_NAV_HEIGHTUpMd + ARTICLE_SECTION_OFFSET
      }px 0px 0px 0px`,
    };

    const entryMap: Record<string, boolean> = {};

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        entryMap[entry.target.id] = entry.isIntersecting;
      });

      let active = "chapter0";
      for (const key in entryMap) {
        if (entryMap[key]) {
          active = key;
          break;
        }
      }

      setActiveItemId(active);
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    const sections = containerRef.current.querySelectorAll(".article-section");

    sections.forEach((section: Element) => observer.observe(section));

    return () => {
      sections.forEach((section: Element) => observer.unobserve(section));
    };
  }, [setActiveItemId, items]);

  const renderContentItem = (contentItem: IContentItem, index: number) => {
    switch (contentItem.type) {
      case "paragraph":
        return (
          <div className="article-content" key={`paragraph-${index}`}>
            {contentItem.content[0]}
          </div>
        );

      case "ordered-list":
        return (
          <ol className="article-ordered-list" key={`ordered-list-${index}`}>
            {contentItem.content.map((item: string, itemIndex: number) => (
              <li key={`ordered-item-${itemIndex}`}>{item}</li>
            ))}
          </ol>
        );

      case "unordered-list":
        return (
          <ul
            className="article-unordered-list"
            key={`unordered-list-${index}`}
          >
            {contentItem.content.map((item: string, itemIndex: number) => (
              <li key={`unordered-item-${itemIndex}`}>{item}</li>
            ))}
          </ul>
        );

      default:
        return (
          <div className="article-content" key={`unknown-${index}`}>
            {Array.isArray(contentItem.content)
              ? contentItem.content.join(" ")
              : JSON.stringify(contentItem.content)}
          </div>
        );
    }
  };

  return (
    <div
      ref={containerRef}
      className="article-main-content"
      style={{
        marginRight: isRTL ? "0" : !downLg ? "120px" : "50px",
        marginLeft: isRTL ? (!downLg ? "120px" : "50px") : "0",
        paddingTop: downMd ? "35px" : "50px",
        direction: i18n.dir(),
      }}
    >
      {items.length === 0 ? (
        <div>{t("no_content_in_article")}</div>
      ) : (
        items.map((item: IConvertedArticleContent) => (
          <div
            key={item.id}
            id={item.id}
            style={{
              marginBottom: downMd ? "35px" : "50px",
            }}
            className="article-section"
          >
            <div
              className="article-chapter"
              style={{
                fontSize: downMd ? "20px" : "24px",
                marginBottom: downMd ? "15px" : "25px",
                lineHeight: downMd ? "24px" : "22px",
              }}
            >
              {item.title}
            </div>
            {Array.isArray(item.content) &&
              item.content.map((contentItem, index) => (
                <div key={index}>
                  {renderContentItem(contentItem, index)}
                  <br />
                </div>
              ))}
          </div>
        ))
      )}
    </div>
  );
};
